// src/components/Team/Team.jsx
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import "./Team.css";

const PLAYER_IMAGE_BASE = "https://unionafricaceuti.com/img/jugadores";
const STAFF_IMAGE_BASE = "https://unionafricaceuti.com/img/cuerpotecnico";

// Código de nacionalidad -> bandera
const NATIONALITY_FLAG = {
  ES: "🇪🇸",
  AR: "🇦🇷",
  BR: "🇧🇷",
  FR: "🇫🇷",
  IT: "🇮🇹",
};

const getFlag = (code) => NATIONALITY_FLAG[code] ?? "";

// Jugadores
const players = [
  { file: "MarioGomezHernandez.png",            number: 1,  position: "Portero", nationality: "ES" },
  { file: "DennisBerthod.png",                  number: 2,  position: "Portero", nationality: "IT" },
  { file: "VictorManuelArevaloCarillo.png",     number: 3,  position: "Cierre",  nationality: "ES" },
  { file: "JaviGonzalezRodriguez.png",        number: 4,  position: "Ala",     nationality: "ES" },
  { file: "NachoTorresSalazar.png",             number: 5,  position: "Ala",     nationality: "ES" },
  { file: "AminMadjidBenslama.png",             number: 7,  position: "Ala",     nationality: "FR" },
  { file: "AlvaroRuanoPrieto.png",              number: 8,  position: "Ala",     nationality: "ES" },
  { file: "AlejandroMoñinoGarcia.png",          number: 9,  position: "Pivot",   nationality: "ES" },
  { file: "DavidSeñoretTorres.png",             number: 10, position: "Ala",     nationality: "ES" },
  { file: "AlejandroGarciaMuñoz.png",           number: 17, position: "Ala",     nationality: "ES" },
  { file: "AlbertoGutierrezPatiño.png",         number: 21, position: "Portero", nationality: "ES" },
  { file: "MarioCarrion.png",                   number: 30, position: "Ala",     nationality: "ES" },
  { file: "JavierMartin.png",                     number: 35, position: "Cierre",  nationality: "ES" },
  { file: "GustavoEnriqueDaSilvaBarbosa.png",   number: 50, position: "Cierre",  nationality: "BR" },
  { file: "GuilhermeDosSantos.png",             number: 97, position: "Pivot",  nationality: "BR" },

];

// Cuerpo técnico
const staff = [
  { file: "IgnacioGarciaCastro.png", role: "Entrenador" },
  { file: "SergioBermúdez.png",      role: "2º Entrenador" },
  { file: "Hicham.png",              role: "Encargado material" },
  { file: "HugoPalomino.png",        role: "Delegado" },
  { file: "IvanGallardo.png",        role: "Psicólogo" },
  { file: "NachoGuerrero.png",       role: "Preparador físico" },
];

// "MarioGomezHernandez.png" -> "Mario Gomez Hernandez"
function fileToFullName(file) {
  const base = file.replace(/\.[^.]+$/, "");
  const parts = base.split(/(?=[A-ZÁÉÍÓÚÑ])/);
  return parts.join(" ");
}

const PersonCard = memo(function PersonCard({
  person,
  type,
  index,
  imageBase,
  onOpen,
}) {
  const [loaded, setLoaded] = useState(false);

  const fullName = person.fullName ?? fileToFullName(person.file);

  // Prioriza las primeras 4 imágenes para que “aparezca” rápido arriba
  const isPriority = index < 4;

  return (
    <article
      className={`player-card card-appear ${type === "staff" ? "staff-card" : ""}`}
      {...(type === "player" ? { "data-number": person.number } : {})}
      style={{ animationDelay: `${index * 45}ms` }}
      onClick={() => onOpen(person, type)}
    >
      <div className="player-photo">
        <div className={`img-skeleton ${loaded ? "is-hidden" : ""}`} />
        <img
          src={`${imageBase}/${person.file}`}
          alt={fullName}
          className={`player-img ${loaded ? "is-loaded" : ""}`}
          loading={isPriority ? "eager" : "lazy"}
          decoding="async"
          fetchpriority={isPriority ? "high" : "auto"}
          onLoad={() => setLoaded(true)}
        />
      </div>

      <div className="player-meta">
        <div className="player-meta-row">
          {type === "player" ? (
            <>
              <span className="player-number-badge">{person.position}</span>
              {getFlag(person.nationality) && (
                <span className="player-flag">{getFlag(person.nationality)}</span>
              )}
            </>
          ) : (
            <>
              {person.role && (
                <span className="player-number-badge staff-role-badge">
                  {person.role}
                </span>
              )}
              <span />
            </>
          )}
        </div>

        <p className="player-name">{fullName}</p>
      </div>
    </article>
  );
});

const Team = () => {
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [modalImgLoaded, setModalImgLoaded] = useState(false);
  const [showStaff, setShowStaff] = useState(false);

  // Prepara listas una vez (fullName + sort)
  const playersPrepared = useMemo(() => {
    return [...players]
      .map((p) => ({ ...p, fullName: fileToFullName(p.file) }))
      .sort((a, b) => a.number - b.number);
  }, []);

  const staffPrepared = useMemo(() => {
    return staff.map((s) => ({ ...s, fullName: fileToFullName(s.file) }));
  }, []);

  // Renderiza staff “en idle” (así primero entra el equipo y luego el staff)
  useEffect(() => {
    let idleId;
    let tId;

    if ("requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(() => setShowStaff(true));
    } else {
      tId = window.setTimeout(() => setShowStaff(true), 150);
    }

    return () => {
      if ("cancelIdleCallback" in window && idleId) window.cancelIdleCallback(idleId);
      if (tId) window.clearTimeout(tId);
    };
  }, []);

  const openPerson = useCallback((person, type) => {
    setModalImgLoaded(false);
    setSelectedPerson({ ...person, type });
  }, []);

  const closePerson = useCallback(() => {
    setSelectedPerson(null);
    setModalImgLoaded(false);
  }, []);

  return (
    <section>
      <div className="max-w-6xl mx-auto px-4 py-6 md:py-10">
        {/* 1er equipo */}
        <h2 className="text-xl md:text-2xl font-semibold mb-6 text-white">
          1er equipo
        </h2>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-6">
          {playersPrepared.map((player, idx) => (
            <PersonCard
              key={player.file}
              person={player}
              type="player"
              index={idx}
              imageBase={PLAYER_IMAGE_BASE}
              onOpen={openPerson}
            />
          ))}
        </div>

        {/* Cuerpo técnico */}
        <h2 className="text-xl md:text-2xl font-semibold mt-10 mb-6 text-white">
          Cuerpo técnico
        </h2>

        {!showStaff ? (
          // Skeleton de sección (para que no “salte”)
          <div className="staff-section-skeleton" aria-hidden="true" />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-6">
            {staffPrepared.map((member, idx) => (
              <PersonCard
                key={member.file}
                person={member}
                type="staff"
                index={idx}
                imageBase={STAFF_IMAGE_BASE}
                onOpen={openPerson}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modal (vale para ambos) */}
      {selectedPerson && (
        <div className="player-modal-overlay" onClick={closePerson}>
          <article
            className={`player-modal-card ${
              selectedPerson.type === "staff" ? "staff-modal" : ""
            }`}
            {...(selectedPerson.type === "player"
              ? { "data-number": selectedPerson.number }
              : {})}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="player-modal-close"
              onClick={closePerson}
              aria-label="Cerrar ficha"
            >
              ×
            </button>

            <div className="player-modal-photo">
              <div className={`img-skeleton ${modalImgLoaded ? "is-hidden" : ""}`} />
              <img
                src={`${
                  selectedPerson.type === "player"
                    ? PLAYER_IMAGE_BASE
                    : STAFF_IMAGE_BASE
                }/${selectedPerson.file}`}
                alt={selectedPerson.fullName}
                className={`player-modal-img ${modalImgLoaded ? "is-loaded" : ""}`}
                decoding="async"
                onLoad={() => setModalImgLoaded(true)}
              />
            </div>

            <div className="player-modal-meta">
              <div className="player-modal-row">
                {selectedPerson.type === "player" ? (
                  <>
                    <span className="player-modal-position">
                      {selectedPerson.position}
                    </span>
                    {getFlag(selectedPerson.nationality) && (
                      <span className="player-flag">
                        {getFlag(selectedPerson.nationality)}
                      </span>
                    )}
                  </>
                ) : (
                  selectedPerson.role && (
                    <span className="player-modal-position">
                      {selectedPerson.role}
                    </span>
                  )
                )}
              </div>

              <h3 className="player-modal-name">{selectedPerson.fullName}</h3>

              {selectedPerson.type === "player" && (
                <p className="player-modal-number">
                  Dorsal {selectedPerson.number}
                </p>
              )}
            </div>
          </article>
        </div>
      )}
    </section>
  );
};

export default Team;
